/**
 * PATH: apps/qa/src/pages/portal/PortalSignaturePad.tsx
 * FILE_ID: QA-PORTAL-SIGNATURE-PAD-001
 * PURPOSE: Capture an anonymous review signature as a PNG File.
 * WHY: The token-bound portal upload accepts multipart bytes, never data URLs.
 */

import { useCallback, useEffect, useRef, useState } from 'react'
import { Check, Trash2 } from 'lucide-react'

export function PortalSignaturePad({ onChange, disabled = false }: { onChange: (file: File | null) => void; disabled?: boolean }) {
  const canvasRef = useRef<HTMLCanvasElement>(null)
  const drawingRef = useRef(false)
  const [captured, setCaptured] = useState(false)

  const context = useCallback(() => {
    const ctx = canvasRef.current?.getContext('2d')
    if (ctx) {
      ctx.strokeStyle = '#0f172a'
      ctx.lineWidth = 2.5
      ctx.lineCap = 'round'
      ctx.lineJoin = 'round'
    }
    return ctx
  }, [])

  useEffect(() => {
    const canvas = canvasRef.current
    if (!canvas) return
    const rect = canvas.getBoundingClientRect()
    const ratio = window.devicePixelRatio || 1
    canvas.width = Math.max(rect.width * ratio, 600)
    canvas.height = Math.max(rect.height * ratio, 200)
    const ctx = context()
    ctx?.scale(ratio, ratio)
  }, [context])

  const point = (event: React.PointerEvent<HTMLCanvasElement>) => {
    const rect = event.currentTarget.getBoundingClientRect()
    return { x: event.clientX - rect.left, y: event.clientY - rect.top }
  }
  const start = (event: React.PointerEvent<HTMLCanvasElement>) => {
    if (disabled) return
    drawingRef.current = true
    event.currentTarget.setPointerCapture?.(event.pointerId)
    const position = point(event)
    const ctx = context()
    ctx?.beginPath()
    ctx?.moveTo(position.x, position.y)
  }
  const draw = (event: React.PointerEvent<HTMLCanvasElement>) => {
    if (!drawingRef.current || disabled) return
    const position = point(event)
    const ctx = context()
    ctx?.lineTo(position.x, position.y)
    ctx?.stroke()
  }
  const finish = () => {
    if (!drawingRef.current || disabled) return
    drawingRef.current = false
    canvasRef.current?.toBlob((blob) => {
      if (!blob) return
      const file = new File([blob], `portal-signature-${Date.now()}.png`, { type: 'image/png' })
      setCaptured(true)
      onChange(file)
    }, 'image/png')
  }
  const clear = () => {
    const canvas = canvasRef.current
    canvas?.getContext('2d')?.clearRect(0, 0, canvas.width, canvas.height)
    setCaptured(false)
    onChange(null)
  }

  return (
    <div className="space-y-2">
      <p className="text-sm font-medium text-slate-700">Draw signature <span className="font-normal text-slate-500">(optional)</span></p>
      <canvas ref={canvasRef} aria-label="Draw signature" className="h-32 w-full touch-none rounded-lg border-2 border-dashed border-slate-300 bg-white disabled:cursor-not-allowed" onPointerDown={start} onPointerMove={draw} onPointerUp={finish} onPointerCancel={finish} />
      <div className="flex items-center justify-between">
        <button type="button" onClick={clear} disabled={!captured || disabled} className="inline-flex items-center gap-1 text-sm text-slate-600 disabled:opacity-40"><Trash2 className="h-4 w-4" />Clear signature</button>
        {captured && <span role="status" className="inline-flex items-center gap-1 text-xs text-emerald-700"><Check className="h-3.5 w-3.5" />PNG ready to upload</span>}
      </div>
      <p className="text-xs text-slate-500">You can submit typed approval without drawing. A drawn signature uploads anonymously through this report token.</p>
    </div>
  )
}
